import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, PenLine } from 'lucide-react';
import type { IncidentType } from '../types';
import type { ClaimTheme } from '../constants';
import { AnimatedButton } from '../shared/AnimatedButton';
import { cn } from '@/lib/utils';

interface NarrativeSectionProps {
  narrative: string;
  incidentType: IncidentType | null;
  onChange: (value: string) => void;
  onContinue: () => void;
  onSkip: () => void;
  onBack: () => void;
  theme?: ClaimTheme;
}

const prompts: Record<string, string[]> = {
  delay: [
    'Welche Pläne wurden durch die Verspätung gestört?',
    'Wie hat die Fluggesellschaft mit Ihnen kommuniziert?',
    'Haben Sie Betreuungsleistungen erhalten?',
  ],
  cancel: [
    'Wann haben Sie von der Annullierung erfahren?',
    'Welche Alternative hat die Fluggesellschaft angeboten?',
    'Wie hat dies Ihre Reisepläne beeinflusst?',
  ],
  denied_boarding: [
    'Welchen Grund hat man Ihnen am Gate genannt?',
    'Wurde Ihnen vor Ort eine Entschädigung angeboten?',
    'Wie haben Sie schließlich Ihr Ziel erreicht?',
  ],
  other: [
    'Beschreiben Sie die Störung im Detail',
    'Wie hat die Fluggesellschaft reagiert?',
    'Welche Auswirkungen hatte dies auf Ihre Reise?',
  ],
};

export function NarrativeSection({
  narrative,
  incidentType,
  onChange,
  onContinue,
  onSkip,
  onBack,
  theme = 'invol',
}: NarrativeSectionProps) {
  const contextPrompts = prompts[incidentType || 'other'] || prompts.other;
  const charCount = narrative.length;
  const maxChars = 2000;

  return (
    <motion.div
      key="narrative"
      initial={{ opacity: 0, x: 30 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -30 }}
      transition={{ duration: 0.3 }}
      className="flex flex-col"
    >
      {/* Back button */}
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        onClick={onBack}
        className="self-start -ml-2 mb-4 p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
      >
        <ArrowLeft className="w-5 h-5" />
      </motion.button>

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="flex items-center gap-4 mb-4"
      >
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-400 to-emerald-500 flex items-center justify-center shadow-lg shadow-emerald-500/25">
          <PenLine className="w-7 h-7 text-white" />
        </div>
        <div>
          <h2 className="text-3xl font-bold text-slate-900">
            Erzählen Sie Ihre Geschichte
          </h2>
          <p className="text-slate-500">
            Helfen Sie uns zu verstehen, wie Ihre Pläne beeinträchtigt wurden
          </p>
        </div>
      </motion.div>

      {/* Prompts */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="flex flex-wrap gap-2 mb-4"
      >
        {contextPrompts.map((prompt, i) => (
          <motion.span
            key={i}
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 + i * 0.05 }}
            className="px-3 py-1.5 rounded-full bg-slate-100 text-sm text-slate-600"
          >
            {prompt}
          </motion.span>
        ))}
      </motion.div>

      {/* Textarea */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="relative mb-6"
      >
        <textarea
          value={narrative}
          onChange={(e) => onChange(e.target.value.slice(0, maxChars))}
          placeholder="Schreiben Sie hier Ihre Geschichte... Was waren Ihre Pläne? Wie hat Sie die Störung beeinträchtigt? Was hat die Fluggesellschaft getan (oder nicht getan)?"
          rows={6}
          className={cn(
            'w-full px-4 py-3 rounded-xl border border-slate-200',
            'text-slate-800 placeholder:text-slate-400',
            'focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400',
            'transition-all resize-none text-base'
          )}
        />
        <div className="absolute bottom-3 right-3 text-xs text-slate-400">
          {charCount}/{maxChars}
        </div>
      </motion.div>

      {/* Actions */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.25 }}
        className="flex items-center justify-between"
      >
        <button
          onClick={onSkip}
          className="text-sm text-slate-500 hover:text-slate-700 transition-colors"
        >
          Vorerst überspringen
        </button>
        <AnimatedButton
          variant="primary"
          size="lg"
          onClick={onContinue}
          icon={<ArrowRight className="w-5 h-5" />}
          theme={theme}
        >
          Weiter zur Überprüfung
        </AnimatedButton>
      </motion.div>
    </motion.div>
  );
}
